// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	_preloadArboriumForTests,
	isSupportedHighlightLanguage,
	useArboriumHighlightedHtml,
} from '@app/features/code_highlighting/utils/ArboriumHighlighting';
import {convertAnsiToHtml} from '@app/features/messaging/components/markdown/renderers/common/AnsiConverter';
import {MarkdownContext, type RendererProps} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import codeElementsStyles from '@app/features/theme/styles/CodeElements.module.css';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {
	MAX_KATEX_RENDER_CONTROL_SEQUENCE_COUNT,
	MAX_KATEX_RENDER_SOURCE_LENGTH,
} from '@app/vendor/constants/LimitConstants';
import type {CodeBlockNode, InlineCodeNode} from '@app/vendor/markdown_parser/types/Nodes';
import {msg} from '@lingui/core/macro';
import {CheckCircleIcon, ClipboardIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import React, {useEffect, useMemo, useState} from 'react';

const logger = new Logger('CodeElementsRenderer');
const KATEX_CODE_BLOCK_LANGUAGES = new Set(['katex', 'latex', 'tex']);
const ANSI_CODE_BLOCK_LANGUAGES = new Set(['ansi']);

const KATEX_CONTROL_SEQUENCE_REGEX = /\\(?:[a-zA-Z@]+|.)/gu;

type KatexModule = typeof import('katex').default;

let katexPromise: Promise<KatexModule> | null = null;

let katexModule: KatexModule | null = null;
function loadKatex(): Promise<KatexModule> {
	if (!katexPromise) {
		katexPromise = (async () => {
			const [{default: kt}] = await Promise.all([import('katex'), import('katex/dist/katex.min.css')]);
			katexModule = kt;
			return kt;
		})();
	}
	return katexPromise;
}
export async function _preloadForTests(): Promise<void> {
	await Promise.all([_preloadArboriumForTests(), loadKatex()]);
}
function useLazyChunkLoad(load: () => Promise<unknown>, isLoaded: boolean, shouldLoad: boolean): void {
	const [, setTick] = useState(0);
	useEffect(() => {
		if (!shouldLoad || isLoaded) {
			return;
		}
		let cancelled = false;
		load().then(() => {
			if (!cancelled) {
				setTick((tick) => tick + 1);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [load, isLoaded, shouldLoad]);
}
function normaliseCodeBlockLanguage(language?: string): string | undefined {
	if (!language) {
		return undefined;
	}
	const [primaryLanguage] = language.trim().split(/\s+/u);
	if (!primaryLanguage) {
		return undefined;
	}
	return primaryLanguage.toLowerCase();
}
function countKatexControlSequences(content: string): number {
	return Array.from(content.matchAll(KATEX_CONTROL_SEQUENCE_REGEX)).length;
}
function shouldRenderKatexSource(content: string): boolean {
	if (content.length > MAX_KATEX_RENDER_SOURCE_LENGTH) {
		return false;
	}
	return countKatexControlSequences(content) <= MAX_KATEX_RENDER_CONTROL_SEQUENCE_COUNT;
}
export function CodeBlockRenderer({node, id, options}: RendererProps<CodeBlockNode>): React.ReactElement {
	const {content} = node;
	if (options.context === MarkdownContext.RESTRICTED_INLINE_REPLY) {
		return (
			<code key={id} className={markupStyles.inline}>
				{content.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()}
			</code>
		);
	}
	return <RichCodeBlockRenderer node={node} id={id} options={options} />;
}
function RichCodeBlockRenderer({
	node,
	id,
	options,
}: Pick<RendererProps<CodeBlockNode>, 'node' | 'id' | 'options'>): React.ReactElement {
	const i18n = options.i18n!;
	const {content, language} = node;
	const normalisedLanguage = normaliseCodeBlockLanguage(language);
	const isKatexBlock = Boolean(normalisedLanguage && KATEX_CODE_BLOCK_LANGUAGES.has(normalisedLanguage));
	const isAnsiBlock = Boolean(normalisedLanguage && ANSI_CODE_BLOCK_LANGUAGES.has(normalisedLanguage));
	const shouldAttemptKatexRender = useMemo(
		() => Boolean(isKatexBlock && shouldRenderKatexSource(content)),
		[content, isKatexBlock],
	);
	const [isCopied, setIsCopied] = useState(false);
	useLazyChunkLoad(loadKatex, katexModule !== null, isKatexBlock && shouldAttemptKatexRender);
	const katex = katexModule;
	const shouldHighlight = !isKatexBlock && !isAnsiBlock && Boolean(normalisedLanguage);
	const highlightedHtml = useArboriumHighlightedHtml(
		shouldHighlight ? normalisedLanguage : null,
		shouldHighlight ? content : null,
	);
	const handleCopy = () => {
		TextCopyCommands.copy(i18n, content);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};
	const copyButton = (
		<div className={markupStyles.codeActions}>
			<button
				type="button"
				onClick={handleCopy}
				aria-label={isCopied ? i18n._(msg`Copied!`) : i18n._(msg`Copy code`)}
				className={clsx(isCopied && markupStyles.codeActionsVisible)}
			>
				{isCopied ? (
					<CheckCircleIcon className={codeElementsStyles.icon} />
				) : (
					<ClipboardIcon className={codeElementsStyles.icon} />
				)}
			</button>
		</div>
	);
	const plainCodeBlock = (
		<div key={id} className={markupStyles.codeContainer}>
			{copyButton}
			<pre>
				<code className={markupStyles.hljs}>{content}</code>
			</pre>
		</div>
	);
	if (isKatexBlock) {
		if (!shouldAttemptKatexRender) {
			return plainCodeBlock;
		}
		if (!katex) {
			return plainCodeBlock;
		}
		try {
			const html = katex.renderToString(content, {
				displayMode: true,
				throwOnError: false,
				errorColor: 'var(--accent-danger)',
				trust: false,
				strict: false,
				output: 'html',
			});
			return (
				<div key={id} className={markupStyles.latexCodeBlock}>
					<div className={markupStyles.codeContainer}>
						{copyButton}
						<div className={markupStyles.latexContent} dangerouslySetInnerHTML={{__html: html}} />
					</div>
				</div>
			);
		} catch (error) {
			logger.error('KaTeX rendering error:', error);
			return (
				<div key={id} className={markupStyles.codeContainer}>
					{copyButton}
					<pre>
						<code className={markupStyles.hljs}>
							{i18n._(msg`Error rendering LaTeX: ${(error as Error).message || i18n._(msg`Unknown error`)}`)}
						</code>
					</pre>
				</div>
			);
		}
	}
	if (isAnsiBlock) {
		const ansiHtml = convertAnsiToHtml(content);
		return (
			<div key={id} className={markupStyles.codeContainer}>
				{copyButton}
				<pre>
					<code
						className={clsx(markupStyles.hljs, markupStyles.ansiCode)}
						dangerouslySetInnerHTML={{__html: ansiHtml}}
					/>
				</pre>
			</div>
		);
	}
	let highlightedContent: React.ReactElement;
	if (normalisedLanguage && isSupportedHighlightLanguage(normalisedLanguage)) {
		highlightedContent = (
			<code className={clsx(markupStyles.hljs, language)} dangerouslySetInnerHTML={{__html: highlightedHtml}} />
		);
	} else {
		highlightedContent = <code className={markupStyles.hljs}>{content}</code>;
	}
	return (
		<div key={id} className={markupStyles.codeContainer}>
			{copyButton}
			<pre>{highlightedContent}</pre>
		</div>
	);
}
export function InlineCodeRenderer({node, id}: RendererProps<InlineCodeNode>): React.ReactElement {
	return React.createElement('code', {key: id, className: markupStyles.inline}, node.content);
}
